import * as wp from "@wordpress/html-entities";
import React, { useEffect } from "react";
import Select from "react-select";
import { useSpring, animated } from "react-spring";
import styled from "styled-components";

import { colors } from "../constants/theme";
import { useSchoolSettings } from "../hooks/useSchoolSettings";
import { useStudyPrograms } from "../hooks/useStudyPrograms";
import { Button, ButtonLink, Col, Row } from "./LayoutBlocks";

const LayoutWrapper = styled.section`
  padding: 1em;
  position: relative;
  z-index: 10;
`;

const CustomCol = styled(Col)`
  min-width: 320px;
`;

const ButtonCol = styled(Col)`
  max-width: 320px;
  min-width: 320px;
  margin-top: 12px;
  ${Button} {
    margin-bottom: 6px;
  }
  @media only screen and (max-width: 320px) {
    max-width: 100%;
  }
`;

const Selector = ({
  selectedCourses,
  onChange,
  activeProgram,
  setSelectives,
  selectives,
  timer,
}) => {
  const { showHours } = useSchoolSettings();

  const reset = () => onChange(null);

  return (
    <LayoutWrapper>
      <Row>
        <CustomCol>
          <ProgramSelector onChange={onChange} activeProgram={activeProgram} />
          {showHours && <HourOverview timer={timer} />}
        </CustomCol>
        <CustomCol>
          <ExtraCoursesSelector
            key={activeProgram?.id} // reset on picking new program
            activeProgram={activeProgram}
            selectives={selectives}
            setSelectives={setSelectives}
            selectedCourses={selectedCourses}
          />
        </CustomCol>
        <ButtonCol>
          {activeProgram && (
            <Buttons reset={reset} activeProgram={activeProgram} />
          )}
        </ButtonCol>
      </Row>
    </LayoutWrapper>
  );
};

const Buttons = ({ reset, activeProgram }) => {
  const print = () => {
    window.print();
  };
  const hrefLink = activeProgram?.acf?.link_til_adgangskortet;
  return (
    <>
      <Button onClick={reset}>Nulstil valg</Button>
      <Button onClick={print}>Print oversigt</Button>
      {hrefLink && (
        <ButtonLink href={hrefLink} target="blank" rel="noopener noreferer">
          Gå til adgangskortet
        </ButtonLink>
      )}
      {hrefLink && (
        <p>
          På adgangskortet kan du se, hvilke videregående uddannelser dit
          studievalg giver adgang til.
        </p>
      )}
    </>
  );
};

const ProgramSelector = ({ onChange, activeProgram }) => {
  const { overskrift, beskrivelse } = useSchoolSettings();
  const { data, loading } = useStudyPrograms();

  useEffect(() => {
    if (activeProgram) {
      window.location.hash = activeProgram.id;
    }
  }, [activeProgram]);

  useEffect(() => {
    const preselectedId =
      window.PRESELECTED_PROGRAM || window.location.hash?.split("#")[1];

    if (!preselectedId) {
      return;
    }

    // eslint-disable-next-line eqeqeq
    const preselectedProgram = data?.find((d) => d.id == preselectedId);

    // eslint-disable-next-line eqeqeq
    if (preselectedProgram) {
      onChange(preselectedProgram);
    }
  }, [data, onChange]);

  return (
    <>
      <h2>{overskrift}</h2>
      <p>{beskrivelse}</p>
      <Select
        value={activeProgram}
        aria-label="Vælg studieretning"
        classNamePrefix="select"
        isLoading={loading}
        isSearchable
        isOptionSelected={({ id }) => activeProgram?.id === id}
        getOptionLabel={({ title }) => wp.decodeEntities(title.rendered)}
        name="studieretning"
        options={data || []}
        onChange={onChange}
      />
    </>
  );
};

// checks if it's already selected, in ANOTHER blok
const isAlreadySelected = ({ selectedCourses, item }) => {
  return selectedCourses.some((s) => {
    if (!s?.fag || !item?.fag) {
      return false;
    }
    return s.blok !== item.blok && s.fag.ID === item.fag.ID;
  });
};

const isInblock = ({ item, blokKey }) => {
  const { blok } = item;

  return blokKey === blok;
};

const getConstraintNotMetError = ({ item, selectedCourses }) => {
  const { afhaengighed, kan_ikke_vaelges } = item;

  if (afhaengighed) {
    const missingCourses = afhaengighed
      .filter((f) => !selectedCourses.some((c) => c?.fag?.ID === f.ID))
      .map((c) => c.post_title);

    if (missingCourses.length) {
      return `Hov, du skal først have valgt ${missingCourses.join(" og ")}.`;
    }
  }
  if (kan_ikke_vaelges) {
    const extraCourses = kan_ikke_vaelges
      .filter((f) => selectedCourses.some((c) => c?.fag?.ID === f.ID))
      .map((c) => c.post_title);

    if (extraCourses.length) {
      return `Hov, du skal først fjerne ${extraCourses.join(" og ")}.`;
    }
  }

  return null;
};

const getConstraintWillBeBrokenError = ({ item, selectives }) => {
  let allNecessaryCourses = [];

  for (const c of selectives) {
    if (c?.afhaengighed) {
      allNecessaryCourses = [
        ...allNecessaryCourses,
        ...c.afhaengighed.map((f) => {
          // add the title of the selective to be used in error
          f.selective_post_title = c?.fag?.post_title;
          return f;
        }),
      ];
    }
  }

  const courseIsNeededIn = allNecessaryCourses
    .filter((f) => f.ID === item?.fag?.ID)
    .map((c) => c.selective_post_title);

  if (!courseIsNeededIn.length) {
    return null;
  }

  return `Hov, dette fag er nødvendigt til ${courseIsNeededIn.join(" og ")}.`;
};

const ExtraCoursesSelector = ({
  activeProgram,
  setSelectives,
  selectives,
  selectedCourses,
}) => {
  const { allowChangeCourses } = useSchoolSettings();

  const hideIllegalCourses = !allowChangeCourses;

  const { overskrift_valgfag, beskrivelse_valgfag } = useSchoolSettings();

  if (!activeProgram) {
    return null;
  }

  const {
    valgfag,
    tekst_til_valgfag_blok_1,
    tekst_til_valgfag_blok_2,
    tekst_til_valgfag_blok_3,
    tekst_til_valgfag_blok_4,
    tekst_til_valgfag_blok_5,
    tekst_til_valgfag_blok_6,
  } = activeProgram.acf || {};

  const blocks = [
    tekst_til_valgfag_blok_1,
    tekst_til_valgfag_blok_2,
    tekst_til_valgfag_blok_3,
    tekst_til_valgfag_blok_4,
    tekst_til_valgfag_blok_5,
    tekst_til_valgfag_blok_6,
  ].filter(Boolean);

  return (
    <>
      <h2>{overskrift_valgfag}</h2>
      <p>{beskrivelse_valgfag}</p>
      {blocks.map((label, idx) => {
        const key = "block-" + (idx + 1);
        const lastSelection = selectives[idx - 1];

        // only show block if last block has been selected
        const hideBlock = !allowChangeCourses && idx !== 0 && !lastSelection;
        if (hideBlock) {
          return null;
        }

        const currentSelection = selectives[idx];

        const options = (valgfag || []).filter((item) => {
          // go through constraints
          const isInCurrentBlock = isInblock({ item, blokKey: key });
          if (!isInCurrentBlock) {
            return false;
          }

          if (hideIllegalCourses) {
            const constraintError = getConstraintNotMetError({
              item,
              selectedCourses,
            });

            if (constraintError) {
              return false;
            }
          }

          return !isAlreadySelected({ item, selectedCourses, blokKey: key });
        });

        const empty = !options.length;

        if (empty) {
          return null;
        }

        const handleSelect = allowChangeCourses
          ? selectHandlerWithAlert({
              selectedCourses,
              idx,
              setSelectives,
              currentSelection,
              selectives,
            })
          : selectHandlerClear({
              selectedCourses,
              idx,
              setSelectives,
              currentSelection,
              selectives,
            });

        return (
          <Select
            key={key}
            aria-label={label}
            placeholder={label}
            classNamePrefix="select"
            isSearchable
            value={currentSelection || null}
            isOptionSelected={({ fag }) => currentSelection?.fag?.ID === fag.ID}
            getOptionLabel={({ fag }) => fag.post_title}
            name={label}
            options={options} // only get courses for this block
            isClearable
            isDisabled={!allowChangeCourses && !!currentSelection}
            onChange={handleSelect}
          />
        );
      })}
    </>
  );
};

const selectHandlerWithAlert =
  ({ selectedCourses, idx, setSelectives, currentSelection, selectives }) =>
  (f) => {
    if (f) {
      // // the course is ADDED
      // // Check if all prerequisit courses have been selected
      const constraintError = getConstraintNotMetError({
        item: f,
        selectedCourses,
      });
      // constraints not met, course cannot be selected
      if (constraintError) {
        window.alert(constraintError);
        setSelectives((ss) => {
          const newSelectives = [...ss];
          newSelectives[idx] = null;
          return newSelectives;
        });
        return;
      }
    } else {
      // the course is REMOVED
      // Check if any course needs this course
      const constraintError = getConstraintWillBeBrokenError({
        item: currentSelection,
        selectives,
      });

      // constraints will be broking, course cannot be removed
      if (constraintError) {
        window.alert(constraintError);
        setSelectives((ss) => {
          const newSelectives = [...ss];
          newSelectives[idx] = currentSelection;
          return newSelectives;
        });
        return;
      }
    }
    setSelectives((ss) => {
      const newSelectives = [...ss];
      newSelectives[idx] = f;
      return newSelectives;
    });
  };

const selectHandlerClear =
  ({ selectedCourses, idx, setSelectives, currentSelection, selectives }) =>
  (f) => {
    setSelectives((ss) => {
      let newSelectives = [...ss];
      // clear all other selections if already chosen
      if (currentSelection) {
        newSelectives = [];
      }
      newSelectives[idx] = f;
      return newSelectives;
    });
  };

const Bar = styled.div`
  position: relative;
  width: 200px;
  height: 25px;
  line-height: 25px;
  background: ${colors.greys[100]};
  border-radius: 5px;
  border: 2px solid white;
  overflow: hidden;
  text-align: center;
`;

const maxHours = 2680;

const colorMap = [
  { limit: 2680, color: "green" },
  { limit: 9999, color: "red" },
];

const HourOverview = ({ timer }) => {
  const total = timer[0] + timer[1] + timer[2];

  const barColor = colorMap.find((c) => c.limit > total)?.color;

  const props = useSpring({ total, fill: total / maxHours, color: barColor });

  return (
    <div>
      <h3>Samlet antal timer</h3>
      <Row style={{ margin: 0 }}>
        <Bar>
          <animated.div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              background: props.color,
              width: props.fill.to((x) => x * 100 + "%"),
            }}
          />
        </Bar>
        <strong style={{ marginLeft: 12, lineHeight: "30px" }}>
          <animated.div style={{ position: "relative" }}>
            {props.total.to((x) => x.toFixed(0))}
          </animated.div>
        </strong>
      </Row>
    </div>
  );
};

export default Selector;
